import React, { useState } from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import PropTypes from 'prop-types';
import Icon from '@components/Icon/Icon';
import FieldForm from './FieldForm';
import FieldItemDisplay from './FieldItemDisplay';
import { INITIAL_FIELD_STATE } from './constants';
import './BorrowingFieldsManager.scss';

/**
 * Component for managing borrowing field definitions at category level
 * Allows adding, editing, and removing dynamic field definitions
 */
const BorrowingFieldsManager = ({
  fieldDefinitions = {},
  onChange,
  disabled = false
}) => {
  const { t } = useLanguage();
  const [editingFieldKey, setEditingFieldKey] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [newField, setNewField] = useState({ ...INITIAL_FIELD_STATE });
  const [showAddForm, setShowAddForm] = useState(false);
  const [draggedField, setDraggedField] = useState(null);
  const [dragOverField, setDragOverField] = useState(null);

  const handleAddField = () => {
    if (fieldDefinitions[newField.key]) {
      alert(t('borrowingFields.manager.keyAlreadyExists') || 'A field with this key already exists');
      return;
    }

    const { key, ...fieldDef } = newField;
    const updatedDefinitions = {
      ...fieldDefinitions,
      [key]: {
        ...fieldDef,
        options: fieldDef.type === 'select' ? fieldDef.options : undefined
      }
    };

    onChange(updatedDefinitions);

    // Reset form
    setNewField({ ...INITIAL_FIELD_STATE });
    setShowAddForm(false);
  };

  const handleDeleteField = (fieldKey) => {
    if (!window.confirm(t('borrowingFields.manager.confirmDelete') || 'Are you sure you want to delete this field?')) {
      return;
    }

    const updatedDefinitions = { ...fieldDefinitions };
    delete updatedDefinitions[fieldKey];
    onChange(updatedDefinitions);
    setEditingFieldKey(null);
    setEditingField(null);
  };

  const handleStartEdit = (fieldKey) => {
    setEditingFieldKey(fieldKey);
    setEditingField({
      key: fieldKey,
      ...fieldDefinitions[fieldKey],
      options: fieldDefinitions[fieldKey].options || []
    });
    setShowAddForm(false);
  };

  const handleCancelEdit = () => {
    setEditingFieldKey(null);
    setEditingField(null);
  };

  const handleSaveEdit = () => {
    const { key, ...fieldDef } = editingField;
    const updatedDefinitions = {
      ...fieldDefinitions,
      [key]: {
        ...fieldDef,
        options: fieldDef.type === 'select' ? fieldDef.options : undefined
      }
    };

    onChange(updatedDefinitions);
    setEditingFieldKey(null);
    setEditingField(null);
  };

  // Drag and Drop handlers
  const handleDragStart = (e, fieldKey) => {
    setDraggedField(fieldKey);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
    e.currentTarget.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove('dragging');
    setDraggedField(null);
    setDragOverField(null);
  };

  const handleDragOver = (e, fieldKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedField && draggedField !== fieldKey) {
      setDragOverField(fieldKey);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverField(null);
  };

  const handleDrop = (e, targetFieldKey) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedField || draggedField === targetFieldKey) {
      setDragOverField(null);
      return;
    }

    // Réorganiser les champs
    const fieldKeys = Object.keys(fieldDefinitions);
    const draggedIndex = fieldKeys.indexOf(draggedField);
    const targetIndex = fieldKeys.indexOf(targetFieldKey);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Créer un nouveau tableau avec le nouvel ordre
    const newOrder = [...fieldKeys];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedField);

    // Reconstruire l'objet fieldDefinitions avec le nouvel ordre
    const reorderedDefinitions = {};
    newOrder.forEach(key => {
      reorderedDefinitions[key] = fieldDefinitions[key];
    });

    onChange(reorderedDefinitions);
    setDragOverField(null);
  };

  return (
    <div className="borrowing-fields-manager">
      <div className="manager-header">
        <h4>{t('borrowingFields.manager.title') || 'Dynamic Borrowing Fields'}</h4>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => {
            setShowAddForm(!showAddForm);
            if (!showAddForm) {
              setEditingFieldKey(null);
              setEditingField(null);
            }
          }}
          disabled={disabled}
        >
          <Icon icon={showAddForm ? 'times' : 'plus'} />
          {' '}
          {showAddForm
            ? (t('common.cancel') || 'Cancel')
            : (t('borrowingFields.manager.addField') || 'Add Field')}
        </button>
      </div>

      <p className="manager-description">
        {t('borrowingFields.manager.description') ||
          'Configure dynamic fields that will be requested during reservation, borrowing, or return of resources in this category.'}
      </p>

      {Object.keys(fieldDefinitions).length > 1 && !showAddForm && !editingFieldKey && (
        <div className="drag-hint">
          <Icon icon="info-circle" />
          {' '}
          {t('borrowingFields.manager.dragHint') || 'Glissez-déposez les champs pour changer leur ordre d\'affichage'}
        </div>
      )}

      {/* Add New Field Form */}
      {showAddForm && (
        <FieldForm
          field={newField}
          onChange={setNewField}
          onSave={handleAddField}
          onCancel={() => setShowAddForm(false)}
          isEditing={false}
        />
      )}

      {/* List of Existing Fields */}
      <div className="fields-list">
        {Object.keys(fieldDefinitions).length === 0 ? (
          <p className="no-fields">
            <Icon icon="info-circle" /> {t('borrowingFields.manager.noFields') || 'No dynamic fields configured yet.'}
          </p>
        ) : (
          Object.entries(fieldDefinitions).map(([fieldKey, fieldDef]) => (
            <div
              key={fieldKey}
              className={`field-item ${
                draggedField === fieldKey ? 'dragging' : ''
              } ${
                dragOverField === fieldKey ? 'drag-over' : ''
              }`}
              draggable={!editingFieldKey}
              onDragStart={(e) => handleDragStart(e, fieldKey)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, fieldKey)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, fieldKey)}
            >
              {editingFieldKey === fieldKey ? (
                // Edit Form
                <FieldForm
                  field={editingField}
                  onChange={setEditingField}
                  onSave={handleSaveEdit}
                  onCancel={handleCancelEdit}
                  isEditing={true}
                />
              ) : (
                // Display Mode
                <FieldItemDisplay
                  fieldKey={fieldKey}
                  fieldDef={fieldDef}
                  onEdit={() => handleStartEdit(fieldKey)}
                  onDelete={() => handleDeleteField(fieldKey)}
                  disabled={disabled}
                />
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

BorrowingFieldsManager.propTypes = {
  fieldDefinitions: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

export default BorrowingFieldsManager;
